import type { IViewPortManipulationApi } from '@/engine/app/interfaces';
import { useMemo } from 'react';
import { FluoroAnglesIntegrationApi } from '../componentsIntegration/FluoroAnglesIntegrationApi';
import { ViewPortContextIntegrationApi } from '../componentsIntegration/ViewPortContextApi';
import { ViewPortManipulationApiComposite } from '../componentsIntegration/ViewPortManipulationApiComposite';
import { useInjection } from 'inversify-react';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service';
import { NavigationControlIntegrationApi } from '../componentsIntegration/NavigationControlIntegrationApi';
import { DefaultOrientationProvider } from '../componentsIntegration/DefaultOrientationProvider';
import { CubeControlIntegrationApi } from '../componentsIntegration/CubeControlIntegrationApi';
import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';

export function useViewPortManipulationApi(): readonly [
  IViewPortManipulationApi,
  NavigationControlIntegrationApi,
  ViewPortContextIntegrationApi,
  FluoroAnglesIntegrationApi,
  CubeControlIntegrationApi
] {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);
  const viewService = useInjection<ViewsService>(TYPES.ViewsService);
  const showFluoroAnglesLabels = useSurgerySpecificUiConfigValue<boolean>('showFluoroAnglesLabels');

  const res = useMemo(() => {
    if (!viewService.currentView) throw new Error('Current view is null');

    const defaultOrientationProvider = new DefaultOrientationProvider();
    defaultOrientationProvider.setViewService(viewService);
    defaultOrientationProvider.setPlanningAlgorithm(planningAlgorithm);

    const navigationControlIntegrationApi = new NavigationControlIntegrationApi(defaultOrientationProvider);

    const cubeControlIntegrationApi = new CubeControlIntegrationApi(defaultOrientationProvider);

    const fluoroAnglesIntegrationApi = new FluoroAnglesIntegrationApi();
    const viewPortContextIntegrationApi = new ViewPortContextIntegrationApi();

    const viewPortManipulationApiComposite = new ViewPortManipulationApiComposite([
      navigationControlIntegrationApi,
      cubeControlIntegrationApi,
      viewPortContextIntegrationApi
    ]);

    if (showFluoroAnglesLabels) {
      viewPortManipulationApiComposite.addAPI(fluoroAnglesIntegrationApi);
    }

    const viewPortManipulationApi = viewPortManipulationApiComposite;

    return [
      viewPortManipulationApi,
      navigationControlIntegrationApi,
      viewPortContextIntegrationApi,
      fluoroAnglesIntegrationApi,
      cubeControlIntegrationApi
    ] as const;
  }, [planningAlgorithm, viewService, showFluoroAnglesLabels]);

  return res;
}
